/**
 * Hook usePlans
 * Hook personalizado para gestionar planes de suscripción
 */

import { useState, useEffect, useCallback } from 'react';
import planService from '../services/plans';
import { ERROR_MESSAGES } from '../constants/subscriptions';

/**
 * Hook para gestionar planes
 * @param {Object} options - Opciones adicionales
 */
export const usePlans = (options = {}) => {
  const { 
    autoRefresh = true, 
    refreshInterval = 600000 // 10 minutos
  } = options;
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  /**
   * Carga los planes
   */
  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await planService.getPlans(true); // Usar caché
      
      if (result.success) {
        setPlans(result.plans);
        setLastUpdated(new Date());
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message || ERROR_MESSAGES.GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refresca los planes (sin caché)
   */
  const refreshPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await planService.getPlans(false); // Sin caché
      
      if (result.success) {
        setPlans(result.plans);
        setLastUpdated(new Date());
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message || ERROR_MESSAGES.GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene un plan por ID
   */
  const getPlanById = useCallback(async (planId) => {
    try {
      const result = await planService.getPlanById(planId);
      return result;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Busca planes
   */
  const searchPlans = useCallback(async (searchText) => {
    try {
      const result = await planService.searchPlans(searchText);
      return result;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Obtiene planes recomendados
   */
  const getRecommendedPlans = useCallback(async (userId = null) => {
    try {
      const result = await planService.getRecommendedPlans(userId);
      return result;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Compara planes
   */
  const comparePlans = useCallback((plansToCompare) => {
    return planService.comparePlans(plansToCompare);
  }, []);

  /**
   * Obtiene el plan más popular
   */
  const getPopularPlan = useCallback(async () => {
    try {
      const result = await planService.getPopularPlan();
      return result;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Filtra planes por precio
   */
  const filterPlansByPrice = useCallback((minPrice, maxPrice) => {
    return plans.filter(plan => 
      (plan.plan_price || plan.precio) >= minPrice && (plan.plan_price || plan.precio) <= maxPrice
    );
  }, [plans]);

  /**
   * Filtra planes por tipo
   */
  const filterPlansByType = useCallback((type) => {
    return plans.filter(plan => plan.tipo === type);
  }, [plans]);

  /**
   * Ordena planes por precio
   */
  const sortPlansByPrice = useCallback((ascending = true) => {
    return [...plans].sort((a, b) => {
      const priceA = a.plan_price || a.precio;
      const priceB = b.plan_price || b.precio;
      return ascending ? priceA - priceB : priceB - priceA;
    });
  }, [plans]);

  /**
   * Obtiene planes por frecuencia
   */
  const getPlansByFrequency = useCallback((frequency) => {
    return plans.filter(plan => plan.frecuencia === frequency);
  }, [plans]);

  /**
   * Verifica si un plan existe
   */
  const planExists = useCallback((planId) => {
    return plans.some(plan => plan.id === planId);
  }, [plans]);

  /**
   * Obtiene el rango de precios
   */
  const getPriceRange = useCallback(() => {
    if (plans.length === 0) return { min: 0, max: 0 };
    
    const prices = plans.map(plan => plan.plan_price || plan.precio);
    return {
      min: Math.min(...prices),
      max: Math.max(...prices)
    };
  }, [plans]);

  // Efecto para cargar los planes
  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Efecto para auto-refresco
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadPlans();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, loadPlans]);

  return {
    // Datos
    plans,
    loading,
    error,
    lastUpdated,
    
    // Métodos
    loadPlans,
    refreshPlans,
    getPlanById,
    searchPlans,
    getRecommendedPlans,
    comparePlans,
    getPopularPlan,
    
    // Utilidades
    filterPlansByPrice,
    filterPlansByType,
    sortPlansByPrice,
    getPlansByFrequency,
    planExists,
    getPriceRange
  };
};

export default usePlans;
